import React, { FC } from "react";
import { useController, useFormContext } from "react-hook-form";

import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import FormHelperText from "@mui/material/FormHelperText";
import InputAdornment from "@mui/material/InputAdornment";

import { ReactComponent as HidePasswordIcon } from "assets/images/hide-password-icon.svg";
import { ReactComponent as ShowPasswordIcon } from "assets/images/show-password-icon.svg";

import { TextInputProps } from "./types";

const StyledFormInput: FC<TextInputProps> = (props) => {
  const {
    name,
    title,
    showRequired,
    rules,
    helperText,
    testId,
    inputRef,
    defaultValue = "",
    ...restInputProps
  } = props;

  const formContext = useFormContext();
  const {
    control,
    formState: { errors: { [name]: { message = "" } = {} } = {} },
  } = formContext;

  const { field } = useController({
    name,
    control,
    rules,
    defaultValue,
  });

  const [showPassword, setShowPassword] = React.useState(false);
  const password = restInputProps.type === "password";

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  return (
    // todo: test all inputs
    <Box mb={2} textAlign="left" sx={{ width: "100%", ...restInputProps.sx }}>
      {title && (
        <Typography variant="subtitle2" color="grey800" mb={1}>
          {title}
          {showRequired && (
            <Typography component="span" variant="subtitle2" color="red700">
              {" "}*
            </Typography>
          )}
        </Typography>
      )}
      <TextField
        {...restInputProps}
        error={!!message}
        onChange={field.onChange}
        value={field.value}
        helperText={typeof message === "string" ? message : ""}
        type={password && showPassword ? "text" : restInputProps.type}
        slotProps={{
          formHelperText: {
            "data-testid": name + "-error-message",
          },
          input: {
            ...restInputProps.slotProps?.input,
            inputRef,
            endAdornment: password && (
              <InputAdornment position="end">
                <IconButton
                  aria-label={
                    showPassword ? "display-password" : "hide-password"
                  }
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  onMouseUp={handleMouseUpPassword}
                  edge="end"
                  sx={{ marginRight: "-6px" }}
                >
                  {showPassword ? <ShowPasswordIcon /> : <HidePasswordIcon />}
                </IconButton>
              </InputAdornment>
            ),
            inputProps: {
              "data-testid": testId || name,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              ...(restInputProps.slotProps?.input as Record<string, any>)
                ?.inputProps,
            },
          },
        }}
      />
      {helperText && (
        <FormHelperText
          sx={{ typography: "subtitle2" }}
          data-testid={name + "-helper-text"}
        >
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
};

export default StyledFormInput;
